import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { DataFilter, DataService, ResourceMode, RoleData, UserData, UserRealmData } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { LocalizationService } from '../../services/localization.service';
import { EnvironmentService } from '../../services/environment.service';
import { PGProductData } from '../../models/booking.model';
import { NgbModal, NgbModalRef, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { map } from 'rxjs';
import { PGUtilities } from '../../utilities/pg-utilities';
import { NotificationsService } from '../../services/notifications.service';
import { TasksService } from '../../services/tasks.service';
import { ExportCsvService } from '../../services/export-csv.service';

@Component({
    selector: 'app-pg-admin-realm-users',
    templateUrl: './pg-admin-realm-users.component.html',
    styleUrls: ['./pg-admin-realm-users.component.scss'],
    standalone: false
})
export class PgAdminRealmUsersComponent implements OnInit, OnChanges {

    @Input() realmId:string;
    @Input() resourceMode:ResourceMode;

    isLoading = false;
    isSavingUserRealm:{ [id:string]: boolean } = {};

    rolesData:{ [id:string]: RoleData } = null;
    rolesIgnoreValuesList:Array<string> = null;
    
    usersWithRole:{ [role:string]: Array<UserRealmData> } = {};
    userRealmData:Array<UserRealmData> = null;
    userRejected:Array<UserRealmData> = null;

    userSectionsExpanded = {}

    idNameSemantic = '{{id}} - {{name}}'
    idLabelSemantic = '{{id}} - {{label}}'
    userResourceSemantic = '{{id}} - {{name}} {{surname}}<br><small class="text-muted">{{email}} {{phone}}</small>'

    roleUserRelationConfig = {
        type: 'N:N',
        modelA: 'User',
        modelB: 'Role',
        joinTable: 'RoleUser'
    }

    constructor(private dataService:DataService, private authService:AuthService, private environmentService:EnvironmentService, private localizationService:LocalizationService, private modalService:NgbModal, private notificationsService:NotificationsService, private exportCsvService:ExportCsvService) { }

    ngOnInit(): void {
        if(this.userRealmData == null) {
            this.loadData()
        }
    }

    ngOnChanges() {
        this.loadData()
    }

    pageSize = 5;
    pageNum = 0;

    atEnd = false;

    realmName:string = null;

    searchValue:string = null;
    appliedSearchValue:string = null;

    loadData() {
        this.realmName = null

        if(this.resourceMode != 'admin') {
            this.realmId = this.authService.user.realm?.id;
        }

        this.realmName = this.environmentService.getRealmName(this.realmId, true);

        this.appliedSearchValue = this.searchValue;

        this.rolesData = {};
        this.rolesIgnoreValuesList = [];

        this.atEnd = false;
        this.pageNum = 0;
        this.atEndRejected = false;
        this.pageNumRejected = 0;
        this.usersWithRole = {}
        this.userRealmData = [];
        this.userRejected = [];

        this.isLoading = true;

        this.dataService.getRolesData().subscribe((data) => {
            let _realmFilter = this.getRealmFilter()

            if(this.resourceMode == 'admin' || _realmFilter == null) {
                this.isLoading = false;
                this.loadMoreData()
            }
            else {
                let _relevantRoles = ['admin','editor','business']
                let _roleNameList = JSON.parse(JSON.stringify(_relevantRoles))

                for(let _item of data) {
                    this.rolesData[_item.id] = _item;

                    if(_item.name != 'base' && _item.name != 'socket') {
                        if(_item.name != 'admin') {
                            _roleNameList.push(_item.name)
                        }
                    }
                    else {
                        this.rolesIgnoreValuesList.push(_item.id)
                    }
                }
                
                this.dataService.getRequest('/data/user/roles/' + _roleNameList.join(',') + '?realm_id=' + _realmFilter).subscribe((data) => {
                    let _idList = []
                    
                    for(let _item of data) {
                        _idList.push(_item.id)
                    }

                    this._getDataObservable(0, 5000, this.appliedSearchValue, _realmFilter, _idList).subscribe((data) => {
                        for(let _item of data) {
                            let _roleUserList = (_item as any).related_user?.related_role_user

                            let _hasRelevantRoles = []

                            if(_roleUserList != null)  {
                                for(let _roleUser of _roleUserList) {
                                    if(_roleUser.realm_id == _realmFilter) {
                                        for(let _role of _relevantRoles) {
                                            if(this.rolesData[_roleUser.role_id]?.name == _role) _hasRelevantRoles.push(_role)
                                        }
                                    }
                                }
                            }

                            if(_hasRelevantRoles.length == 0) {
                                let _role = 'other';

                                if(this.usersWithRole[_role] == null) this.usersWithRole[_role] = []
                                this.usersWithRole[_role].push(_item)
                            }
                            else {
                                for(let _role of _hasRelevantRoles) {
                                    if(this.usersWithRole[_role] == null) this.usersWithRole[_role] = []
                                    this.usersWithRole[_role].push(_item)
                                }
                            }
                        }

                        for(let i in this.usersWithRole) {
                            if(i == 'other') {
                                this.usersWithRole[i].sort((a, b) => {
                                    let _aIndex = this._getUserRealmDataSortValue(a, _idList);
                                    let _bIndex = this._getUserRealmDataSortValue(b, _idList);

                                    if(_aIndex < _bIndex) return -1
                                    else if(_aIndex > _bIndex) return 1
                                    else if(a.user_id < b.user_id) return 1
                                    else if(a.user_id > b.user_id) return -1
                                    else return 0
                                })
                            }
                            else {
                                if(i != 'business') {
                                    this.userSectionsExpanded['role-' + i] = true;
                                }

                                this.usersWithRole[i].sort((a, b) => {
                                    if(a.user_id < b.user_id) return 1
                                    else if(a.user_id > b.user_id) return -1
                                    else return 0
                                })
                            }
                        }

                        this.isLoading = false;
                        this.loadMoreData()
                    })
                })
            }
        })
    }

    private _getUserRealmDataSortValue(value:any, idList:Array<any>) {
        let _result = Infinity;

        try {

            for(let _item of value.related_user.related_role) {
                let _index = idList.indexOf(_item.id)
                if(_index != -1 && _result > _index) {
                    _result = _index;
                }
            }
        }
        catch(ex) {
        }

        return _result;
    }

    private _getDataObservable (pageNum:number, pageSize:number, searchString:string, realmFilter:string, inFilter?:Array<string>, rejectedFilter?:boolean) {
        let _filter:Array<DataFilter> = [];
        let _with = ['Profile','RoleUser']

        if(realmFilter) {
            _with.push('UserRealm')
            _filter.push({ resource: 'UserRealm', field: 'realm_id', operator: '==', value: [ realmFilter ] })
        }

        if(inFilter) {
            _filter.push({ field: 'id', operator: 'in', value: inFilter })
        }

        if(rejectedFilter) {
            _filter.push({ resource: 'UserRealm', field: 'status', operator: '==', value: ['rejected'] })
        }
        else {
            _filter.push({ resource: 'UserRealm', field: 'status', operator: '!=', value: ['rejected'] })
        }

        return this.dataService.getResourceData('User', {
            limit: pageSize,
            with: _with,
            offset: pageNum * pageSize,
            filter: _filter,
            search: searchString
        }, null, null, this.resourceMode).pipe(map((data) => {
            let _result:Array<UserRealmData> = []

            for(let _user of data) {
                if(!realmFilter) {
                    _result.push({
                        related_user: _user,
                        related_profile: _user.related_profile,
                    } as any)
                }
                else {
                    let _item = null;
                    
                    for(let _userRealm of _user.related_user_realm) {
                        if(_userRealm.realm_id == realmFilter) {
                            _item = _userRealm;
                            break;
                        }
                    }

                    if(_item != null) {
                        delete _user.related_user_realm;
                        _item.related_user = _user;
                        _item.related_profile = _user.related_profile;

                        _result.push(_item)
                    }
                }

                if(this.resourceMode != 'admin') {
                    this._addUserRole(_user, realmFilter)
                }
            }

            return _result;
        }))
    }

    private _addUserRole(user, realmId) {
        let _relatedRoles = []

        for(let _userRole of user.related_role_user) {
            if(_userRole.realm_id == realmId) {
                if(this.rolesData[_userRole.role_id] != null) {
                    let _role = JSON.parse(JSON.stringify(this.rolesData[_userRole.role_id]))
                    _role.pivot = _userRole;
                    _relatedRoles.push(_role)
                }
            }
        }

        user.related_role = _relatedRoles;
    }

    isLoadingUsers = false;

    loadMoreData() {
        if(!this.isLoadingUsers) {
            this.isLoadingUsers = true;

            this._getDataObservable(this.pageNum, this.pageSize, this.appliedSearchValue, this.getRealmFilter()).subscribe((data) => {
                for(let _item of data) {
                    if(_item.status == 'rejected') {
                        this.userRejected.push(_item)
                    }
                    else {
                        let _hasRole = false;

                        for(let i in this.usersWithRole) {
                            for(let _check of this.usersWithRole[i]) {
                                if(_check.id == _item.id) {
                                    _hasRole = true;
                                    break;
                                }
                            }

                            if(_hasRole) break;
                        }
    
                        if(!_hasRole) this.userRealmData.push(_item)
                    }
                }

                this.isLoadingUsers = false;

                this.pageNum++;
                this.atEnd = data.length < this.pageSize;
            }) 
        }
    }

    pageNumRejected = 0;

    isLoadingRejected = false;
    atEndRejected = false;

    loadMoreDataRejected() {
        if(!this.isLoadingRejected) {
            this.isLoadingRejected = true;

            this._getDataObservable(this.pageNumRejected, this.pageSize, this.appliedSearchValue, this.getRealmFilter(), null, true).subscribe((data) => {
                for(let _item of data) {
                    this.userRejected.push(_item)
                }

                this.isLoadingRejected = false;

                this.pageNumRejected++;
                this.atEndRejected = data.length < this.pageSize;
            }) 
        }
    }

    setUserRealmStatus(userRealm:UserRealmData, status:string) {
        if(userRealm.id == null) {
            this.dataService.postResourceData('UserRealm', { status: status }, this.resourceMode).subscribe((data) => {
                userRealm.id = data.id;
                userRealm.status = data.status;
            })
        }
        else {
            if(!this.isSavingUserRealm[userRealm.id]) {
                this.isSavingUserRealm[userRealm.id] = true;
    
                this.dataService.putElementData('UserRealm', userRealm.id, { status: status }, this.resourceMode).subscribe((data) => {
                    delete this.isSavingUserRealm[userRealm.id]
    
                    userRealm.status = data.status;
                })
            }
        }
    }

    addUser:string = null;

    doAddUser() {
        if(this.addUser) {
            this.isLoading = true; 
            this.dataService.postResourceData('UserRealm', { user_id: this.addUser, realm_id: this.getRealmFilter(), status: 'accepted' }, this.resourceMode).subscribe((data) => {
                this.isLoading = false;
    
                this.loadData()
            })
        }
    }

    getRealmFilter() {
        if(this.resourceMode == 'admin') return this.realmId;
        else return this.authService.user.realm?.id;
    }

    @ViewChild('searchBusinessModal') searchBusinessModal:ElementRef

    private _searchBusinessModalRef:NgbModalRef = null;

    searchUserBusinessLoading = false;

    searchUserBusinessTarget = null;
    searchUserBusinessData:{ [resource:string]: Array<PGProductData> }

    searchUserBusinessOpen(user:UserData) {
        this.searchUserBusinessTarget = user;

        this.searchUserBusinessData = {
            Host: [],
            Eatery: [],
            ExperienceSupplier: []
        }

        let _emailFilter:DataFilter = null;
        let _phoneFilter:DataFilter = null;

        if(user.email) {
            _emailFilter = { field: 'email', operator: '==', value: [ user.email ] }
        }

        if(user.telephone) {
            _phoneFilter = { field: 'phone', operator: '==', value: [ user.telephone ] }
        }

        // TODO: aggiungere check per landline

        this.searchUserBusinessLoading = true;
        let _reqCount = 0;

        for(let i in this.searchUserBusinessData) {
            _reqCount++;
            this.dataService.getResourceData(i, { limit: 5000, filter: [{ 
                field: 'user_id', operator: '==', value: [ user.id ]
            }] }).subscribe((data) => {
                this.searchUserBusinessData[i] = this.searchUserBusinessData[i].concat(data)
                _reqCount--
                if(_reqCount == 0) {
                    this.searchUserBusinessLoading = false;
                    this._searchUserBusinessDataClean()
                }
            })

            if(_emailFilter != null) {
                _reqCount++;
                this.dataService.getResourceData(i, { limit: 5000, filter: [_emailFilter] }).subscribe((data) => {
                    this.searchUserBusinessData[i] = this.searchUserBusinessData[i].concat(data)
                    _reqCount--
                    if(_reqCount == 0) {
                        this.searchUserBusinessLoading = false;
                        this._searchUserBusinessDataClean()
                    }
                })
            }

            if(_phoneFilter != null) {
                this.dataService.getResourceData(i, { limit: 5000, filter: [_phoneFilter] }).subscribe((data) => {
                    this.searchUserBusinessData[i] = this.searchUserBusinessData[i].concat(data)
                    _reqCount--
                    if(_reqCount == 0) {
                        this.searchUserBusinessLoading = false;
                        this._searchUserBusinessDataClean()
                    }
                });
            }
        }

        if(_reqCount == 0) {
            this.searchUserBusinessLoading = false;
        }

        this._searchBusinessModalRef = this.modalService.open(this.searchBusinessModal, { size: 'xl' })
    }

    private _searchUserBusinessDataClean() {
        for(let i in this.searchUserBusinessData) {
            this.searchUserBusinessData[i].sort((a, b) => {
                if(a.id < b.id) return -1;
                else if(a.id > b.id) return 1;
                else return 0;
            })

            for(let j = this.searchUserBusinessData[i].length - 1; j >= 1; j--) {
                if(this.searchUserBusinessData[i][j].id == this.searchUserBusinessData[i][j - 1].id) this.searchUserBusinessData[i].splice(j, 1);
            }
        }
    }

    searchUserBusinessHasNoData() {
        for(let i in this.searchUserBusinessData) {
            if(this.searchUserBusinessData[i].length > 0) return false;
        }

        return true;
    }

    searchUserBusinessDismiss() {
        this._searchBusinessModalRef.dismiss()
    }

    getUserLabel(user:UserData) {
        let _result = ''

        if(user.name) {
            _result = user.name
        }

        if(user.surname) {
            if(_result) _result += ' ';
            _result += user.surname
        }

        if(_result) {
            _result = user.id + ' - ' + _result
        }
        else {
            _result = user.id;
        }

        return _result;
    }

    searchUserBusinessIsSaving = false;

    searchUserBusinessSetUser(resource:string, element:any) {
        this.searchUserBusinessIsSaving = true;

        this.dataService.putElementData(resource, element.id, { user_id: this.searchUserBusinessTarget.id }).subscribe((data) => {
            this.searchUserBusinessIsSaving = false;
            element.user_id = data.user_id;
            element.user = data.user;
        }, () => {
            this.searchUserBusinessIsSaving = false;
        })
    }

    @ViewChild('copyClipboardTooltip') copyClipboardTooltip:NgbTooltip;

    private _copyClipboardTooltipTimeout = null;

    showCopyClipboardTooltip() {
        clearTimeout(this._copyClipboardTooltipTimeout);

        this.copyClipboardTooltip.open();

        this._copyClipboardTooltipTimeout = setTimeout(() => {
            this.copyClipboardTooltip.close();
        }, 2000)
    } 

    getApplicationInviteLink(application:'Forms'|'Entourance') {
        return this.environmentService.getApplicationURL(application)  + '/realms/' + this.realmName
    }

    @ViewChild('sendInviteEmailModal') sendInviteEmailModal:ElementRef

    private _sendInviteEmailModalRef:NgbModalRef = null;

    sendInviteRoleOptions:Array<{ value: string, text: string, tooltip?:string }> = []
    sendInviteRoleValue:'business'|'editor' = null;
    sendInviteEmailAddress:string = null;
    sendInvitePendingInvites:Array<any> = null;
    
    isLoadingPendingInvites = false;

    sendInviteEmailOpen() {
        this.sendInviteRoleOptions = []

        for(let _role of ['business','editor']) {
            this.sendInviteRoleOptions.push({
                value: _role,
                text: this.localizationService.translate('pg-admin-realm-users.send-email-invite-role-' + _role),
                tooltip: this.localizationService.translate('pg-admin-realm-users.send-email-invite-role-tooltip-' + _role),
            })
        }

        this.sendInviteRoleValue = 'business'
        this.sendInviteEmailAddress = '';
        this.submitSendInviteEmailError = false;

        this._sendInviteEmailModalRef = this.modalService.open(this.sendInviteEmailModal)

        this.sendInvitePendingInvites = null;
        this.isLoadingPendingInvites = true;

        this.dataService.getPendingInvites(this.realmId).subscribe((data) => {
            this.isLoadingPendingInvites = false;
            this.sendInvitePendingInvites = data;
        })
    }

    sendInviteEmailDismiss() {
        this._sendInviteEmailModalRef.dismiss();
    }

    canSubmitSendInviteEmail() {
        return this.sendInviteRoleValue != null && PGUtilities.emailRegExp.test(this.sendInviteEmailAddress)
    }

    isSubmittingSendInviteEmail = false;
    submitSendInviteEmailError = false;

    doSubmitSendInviteEmail() {
        this.isSubmittingSendInviteEmail = true;

        this.dataService.sendInviteEmail(this.realmId, this.sendInviteEmailAddress, this.localizationService.currentLanguage, this.sendInviteRoleValue).subscribe((data) => {
            this.isSubmittingSendInviteEmail = false;

            if(data) {
                this.sendInviteEmailDismiss()
                this.notificationsService.addLocalNotification('pg-admin-realm-users.send-email-invite-success', 'success', null, null, 3000)
            }
            else {
                this.submitSendInviteEmailError = true;
            }
        })
    }

    @ViewChild('sendMessageModal') sendMessageModal:ElementRef

    private _sendMessageModalRef:NgbModalRef = null;

    sendMessageSubject:string = null;
    sendMessageContent:string = null;
    
    sendMessageOpen() {
        this.sendMessageSubject = null;
        this.sendMessageContent = null;
        this._sendMessageModalRef = this.modalService.open(this.sendMessageModal)   
    }

    sendMessageDismiss() {
        this._sendMessageModalRef.dismiss();
    }

    isSubmittingSendMessage = false;
    submitSendMessageError = false;

    canSubmitSendMessage() {
        return this.sendMessageContent != null && this.sendMessageContent != '';
    }

    doSubmitSendMessage() {
        this.isSubmittingSendMessage = true;

        this.dataService.broadcastMessage('realm', this.sendMessageContent, this.sendMessageSubject).subscribe((data) => {
            this.isSubmittingSendMessage = false;

            if(data) {
                this.sendMessageDismiss()
                this.notificationsService.addLocalNotification('pg-admin-realm-users.send-message-success', 'success', null, null, 3000)
            }
            else {
                this.submitSendMessageError = true;
            }
        })
    }

    exportUsers() {
        let _output = []

        let _propList = ['related_user.id', 'related_user.name', 'related_user.surname', 'related_user.email', 'related_user.telephone', 'related_profile.company', 'related_profile.vat_number']

        let _header = [this.localizationService.translate('RESOURCES.Role.name')]

        for(let _prop of _propList) {
            let _split = _prop.split('.')

            let _resource = _split[0].split('_')[1]
            _resource = _resource.charAt(0).toUpperCase() + _resource.substring(1)

            _header.push(this.localizationService.translate('RESOURCES.' + _resource + '.fields.' + _split[1] + '.label'))
        }

        _output.push(_header)

        for(let i in this.usersWithRole) {
            let _role = this.localizationService.translate('pg-admin-realm-users.users-' + (i == 'other' ? 'other-roles': (i + '-role')));

            for(let _user of this.usersWithRole[i]) {
                let _row = [_role]

                for(let _prop of _propList) {
                    let _split = _prop.split('.')

                    let _value:any = _user;

                    while(_split.length > 0) {
                        if(_value == null) break;
                        else {
                            _value = _value[_split[0]]
                            _split.shift()
                        }
                    }

                    _row.push(_value)
                }

                _output.push(_row)
            }
        }

        this.exportCsvService.exportArray('Users_Realm_' + this.realmId, _output)
    }

    deleteUserRealm(userRealm:UserRealmData) {
        if(userRealm.id != null) {
            if(!this.isSavingUserRealm[userRealm.id]) {
                this.isSavingUserRealm[userRealm.id] = true;
    
                this.dataService.deleteRequest('/realm/user/' + userRealm.id).subscribe((data) => {
                    delete this.isSavingUserRealm[userRealm.id]
    
                    this.loadData()
                })
            }
        }
    }

    getRoleIcon(role:string) {
        switch(role) {
            case 'admin': return 'fa-user-crown';
            case 'editor': return 'fa-user-hat-tie';
            case 'business': return 'fa-user-tie';
            default: return 'fa-user';
        }
    }
}
